import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import PatientService from "../services/PatientService";
import { Patient, NewPatient } from "../types";
import {
  OwcTypography,
  OwcTable,
  OwcTableBody,
  OwcTableCell,
  OwcTableHeader,
  OwcTableHeaderCell,
  OwcTableRow,
  OwcInput,
  OwcButton,
  OwcProgressSpinner,
  OwcComponentHeader,
  OwcComponentFooter,
  OwcIcon,
  OwcIconButton,
  OwcModalDialog,
  OwcAssistiveText,
  OwcCard,
  OwcCheckbox,
} from "@one/react";

const PatientList: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const tableRef = useRef<HTMLOwcTableElement>(null);
  // We're tracking selected rows but not showing action buttons anymore
  const [, setSelectedRows] = useState<number[]>([]);
  const [sortField, setSortField] = useState<string>("lastName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Enrollment modal state
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [enrollmentForm, setEnrollmentForm] = useState({
    firstName: "",
    lastName: "",
    mrn: "",
    email: "",
    phone: "",
    dateOfBirth: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const queryClient = useQueryClient();

  // Mutation for creating a patient
  const createPatientMutation = useMutation(
    (newPatient: NewPatient) => PatientService.createPatient(newPatient),
    {
      onSuccess: () => {
        // Invalidate and refetch patients list
        queryClient.invalidateQueries("patients");
        setIsEnrollModalOpen(false);
        clearEnrollmentForm();
      },
      onError: (error) => {
        console.error("Failed to create patient:", error);
        // Set error message
        setFormErrors({
          submit:
            "An error occurred while creating the patient. Please try again.",
        });
      },
    }
  );

  const handleEnrollModalVisibleChange = (event: CustomEvent<boolean>) => {
    setIsEnrollModalOpen(event.detail);
    if (!event.detail) {
      clearEnrollmentForm();
    }
  };

  const clearEnrollmentForm = () => {
    setEnrollmentForm({
      firstName: "",
      lastName: "",
      mrn: "",
      email: "",
      phone: "",
      dateOfBirth: "",
    });
    setFormErrors({});
  };

  const handleInputChange = (e: any) => {
    const target = e.target as HTMLInputElement;
    setEnrollmentForm({
      ...enrollmentForm,
      [target.name]: target.value,
    });
    // Clear error for this field if it exists
    if (formErrors[target.name]) {
      setFormErrors({
        ...formErrors,
        [target.name]: "",
      });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!enrollmentForm.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    if (!enrollmentForm.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    if (!enrollmentForm.mrn.trim()) {
      errors.mrn = "MRN is required";
    }

    if (!enrollmentForm.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(enrollmentForm.email)) {
      errors.email = "Invalid email format";
    }

    if (!enrollmentForm.phone.trim()) {
      errors.phone = "Phone number is required";
    }

    if (!enrollmentForm.dateOfBirth.trim()) {
      errors.dateOfBirth = "Date of birth is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const newPatient: NewPatient = {
        ...enrollmentForm,
        sex: "female",
      };

      createPatientMutation.mutate(newPatient);
    }
  };

  const {
    data: patients,
    isLoading,
    error,
    refetch,
  } = useQuery<Patient[]>("patients", PatientService.getPatients);

  const filteredPatients = React.useMemo(() => {
    if (!patients) return [];

    if (!searchTerm) return patients;

    const lowerCaseSearch = searchTerm.toLowerCase();
    return patients.filter(
      (patient) =>
        patient.firstName.toLowerCase().includes(lowerCaseSearch) ||
        patient.lastName.toLowerCase().includes(lowerCaseSearch) ||
        patient.mrn.toLowerCase().includes(lowerCaseSearch)
    );
  }, [patients, searchTerm]);

  // Sort patients based on current sort field and order
  const sortedPatients = React.useMemo(() => {
    if (!filteredPatients) return [];

    return [...filteredPatients].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Handle nested properties
      if (sortField === "lastBloodGlucose") {
        aValue = a.lastBloodGlucose?.value || 0;
        bValue = b.lastBloodGlucose?.value || 0;
      } else if (sortField === "weight") {
        aValue = a.weight?.value || 0;
        bValue = b.weight?.value || 0;
      } else if (sortField === "name") {
        aValue = `${a.lastName} ${a.firstName}`.toLowerCase();
        bValue = `${b.lastName} ${b.firstName}`.toLowerCase();
      } else {
        aValue = (a as any)[sortField];
        bValue = (b as any)[sortField];

        // Convert strings to lowercase for case-insensitive comparison
        if (typeof aValue === "string") {
          aValue = aValue.toLowerCase();
        }
        if (typeof bValue === "string") {
          bValue = bValue.toLowerCase();
        }
      }

      // Handle dates
      if (sortField === "dateOfBirth") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Perform comparison
      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredPatients, sortField, sortOrder]);

  // Paginate patients
  const paginatedPatients = React.useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedPatients.slice(startIndex, endIndex);
  }, [sortedPatients, currentPage]);

  const handlePatientClick = (patientId: string) => {
    navigate(`/patients/${patientId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new sort field with default ascending order
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleSelectedRowsChange = (event: CustomEvent<number[]>) => {
    setSelectedRows(event.detail);
  };

  if (isLoading) {
    return (
      <div
        className="owc-flex owc-justify-center owc-align-center"
        style={{ minHeight: "400px" }}
      >
        <OwcProgressSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="owc-p-3">
        <OwcTypography
          style={{
            color: "var(--owc-color-critical)",
            fontSize: "1.5rem",
            fontWeight: "bold",
          }}
        >
          Error loading patients. Please try again later.
        </OwcTypography>
      </div>
    );
  }

  return (
    <>
      <OwcTable
        ref={tableRef}
        showSelectionColumn
        onSelectedRowsChange={handleSelectedRowsChange}
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          flex: 1,
          minWidth: "800px", // Prevent squishing on narrow screens
          minHeight: "500px", // Minimum height to prevent collapsing
        }}
      >
        <OwcTableHeader sticky>
          <OwcTableHeaderCell
            width="20%"
            align="left"
            resizable
            sortable
            sortOrder={sortField === "name" ? sortOrder : undefined}
            onClick={() => handleSort("name")}
          >
            Name
          </OwcTableHeaderCell>
          <OwcTableHeaderCell
            width="10%"
            align="left"
            resizable
            sortable
            sortOrder={sortField === "sex" ? sortOrder : undefined}
            onClick={() => handleSort("sex")}
          >
            Sex
          </OwcTableHeaderCell>
          <OwcTableHeaderCell
            width="15%"
            align="left"
            resizable
            sortable
            sortOrder={sortField === "dateOfBirth" ? sortOrder : undefined}
            onClick={() => handleSort("dateOfBirth")}
          >
            Date of Birth
          </OwcTableHeaderCell>
          <OwcTableHeaderCell
            width="15%"
            align="left"
            resizable
            sortable
            sortOrder={sortField === "mrn" ? sortOrder : undefined}
            onClick={() => handleSort("mrn")}
          >
            MRN
          </OwcTableHeaderCell>
          <OwcTableHeaderCell
            width="15%"
            align="left"
            resizable
            sortable
            sortOrder={sortField === "lastBloodGlucose" ? sortOrder : undefined}
            onClick={() => handleSort("lastBloodGlucose")}
          >
            Last Blood Glucose
          </OwcTableHeaderCell>
          <OwcTableHeaderCell
            width="15%"
            align="left"
            resizable
            sortable
            sortOrder={sortField === "weight" ? sortOrder : undefined}
            onClick={() => handleSort("weight")}
          >
            Weight
          </OwcTableHeaderCell>
          <OwcTableHeaderCell
            width="10%"
            align="left"
            resizable
            sortable
            sortOrder={sortField === "weeksOfPregnancy" ? sortOrder : undefined}
            onClick={() => handleSort("weeksOfPregnancy")}
          >
            Weeks of Pregnancy
          </OwcTableHeaderCell>
        </OwcTableHeader>
        <OwcTableBody>
          {paginatedPatients.length === 0 ? (
            <OwcTableRow>
              <OwcTableCell style={{ width: "100%" }}>
                <div
                  className="owc-flex owc-justify-center owc-align-center"
                  style={{ height: "200px", width: "100%" }}
                >
                  <OwcTypography style={{ fontSize: "1.25rem" }}>
                    {searchTerm
                      ? "No matching patients found"
                      : "No patients available"}
                  </OwcTypography>
                </div>
              </OwcTableCell>
            </OwcTableRow>
          ) : (
            paginatedPatients.map((patient) => (
              <OwcTableRow
                key={patient.id}
                onClick={() => handlePatientClick(patient.id)}
                style={{ cursor: "pointer" }}
                selectable
                expandable
              >
                <OwcTableCell>
                  {patient.firstName} {patient.lastName}
                </OwcTableCell>
                <OwcTableCell>{patient.sex}</OwcTableCell>
                <OwcTableCell>{formatDate(patient.dateOfBirth)}</OwcTableCell>
                <OwcTableCell>{patient.mrn}</OwcTableCell>
                <OwcTableCell>
                  {patient.lastBloodGlucose
                    ? `${patient.lastBloodGlucose.value} ${patient.lastBloodGlucose.unit}`
                    : "N/A"}
                </OwcTableCell>
                <OwcTableCell>
                  {patient.weight
                    ? `${patient.weight.value} ${patient.weight.unit}`
                    : "N/A"}
                </OwcTableCell>
                <OwcTableCell>{patient.weeksOfPregnancy || "N/A"}</OwcTableCell>

                {/* Expanded content for each row */}
                <div slot="expanded">
                  <div className="owc-p-3">
                    <div className="owc-content-card owc-p-3">
                      <div className="owc-mb-2">
                        <OwcTypography style={{ fontWeight: "bold" }}>
                          Patient Details:
                        </OwcTypography>
                      </div>
                      <div className="owc-flex owc-gap-lg">
                        <div>
                          <div className="owc-mb-1">
                            <OwcTypography>
                              Patient ID: {patient.id}
                            </OwcTypography>
                          </div>
                          <div className="owc-mb-1">
                            <OwcTypography>
                              Full Name: {patient.firstName} {patient.lastName}
                            </OwcTypography>
                          </div>
                          <div className="owc-mb-1">
                            <OwcTypography>Sex: {patient.sex}</OwcTypography>
                          </div>
                        </div>
                        <div>
                          <div className="owc-mb-1">
                            <OwcTypography>MRN: {patient.mrn}</OwcTypography>
                          </div>
                          <div className="owc-mb-1">
                            <OwcTypography>
                              Date of Birth: {formatDate(patient.dateOfBirth)}
                            </OwcTypography>
                          </div>
                          {patient.weeksOfPregnancy && (
                            <div className="owc-mb-1">
                              <OwcTypography>
                                Weeks of Pregnancy: {patient.weeksOfPregnancy}
                              </OwcTypography>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="owc-flex owc-justify-center owc-mt-3">
                        <OwcButton
                          variant="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePatientClick(patient.id);
                          }}
                        >
                          View Full Details
                        </OwcButton>
                      </div>
                    </div>
                  </div>
                </div>
              </OwcTableRow>
            ))
          )}
        </OwcTableBody>
        <OwcComponentHeader slot="header">
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <OwcIcon
              family="outlined"
              name="table_chart"
              status="inherit"
            ></OwcIcon>
            <OwcTypography style={{ fontWeight: "bold" }}>
              Patient List
            </OwcTypography>
          </div>
          <div
            slot="suffix"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexGrow: 1,
              maxWidth: "500px",
            }}
          >
            <OwcInput
              placeholder="Search patients..."
              value={searchTerm}
              onInput={(e) => {
                const target = e.target as HTMLInputElement;
                setSearchTerm(target.value);
              }}
              style={{ width: "100%" }}
            />
            {searchTerm && (
              <OwcButton
                variant="secondary"
                size="medium"
                onClick={() => setSearchTerm("")}
              >
                Clear
              </OwcButton>
            )}
            <OwcIconButton
              family="outlined"
              icon="search"
              aria-label="Search"
            ></OwcIconButton>
          </div>
        </OwcComponentHeader>
        <OwcComponentFooter slot="footer">
          <div slot="prefix" className="owc-flex owc-align-center">
            <OwcButton
              variant="tertiary"
              onClick={() => {
                // Refresh data using React Query refetch
                refetch();
                console.log("Refreshing patient data");
              }}
            >
              <div className="owc-flex owc-align-center owc-gap-sm">
                <OwcIcon name="refresh" family="outlined"></OwcIcon>
                <span>Refresh</span>
              </div>
            </OwcButton>
          </div>

          <div className="owc-flex owc-align-center owc-gap-md">
            <OwcTypography>
              Showing {Math.min(paginatedPatients.length, rowsPerPage)} of{" "}
              {sortedPatients.length} results
            </OwcTypography>

            <div className="owc-flex owc-gap-sm">
              <OwcButton
                variant="secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </OwcButton>
              <OwcTypography
                style={{
                  padding: "0 8px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Page {currentPage} of{" "}
                {Math.ceil(sortedPatients.length / rowsPerPage) || 1}
              </OwcTypography>
              <OwcButton
                variant="secondary"
                disabled={
                  currentPage >= Math.ceil(sortedPatients.length / rowsPerPage)
                }
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Next
              </OwcButton>
            </div>
          </div>

          <div slot="suffix">
            <OwcButton
              variant="primary"
              onClick={() => {
                setIsEnrollModalOpen(true);
              }}
            >
              <div className="owc-flex owc-align-center owc-gap-sm">
                <OwcIcon
                  name="circle_add"
                  family="outlined"
                  status="inherit"
                ></OwcIcon>
                <span>Enroll Patient</span>
              </div>
            </OwcButton>
          </div>
        </OwcComponentFooter>

        {/* Enroll Patient Modal */}
        <OwcModalDialog
          visible={isEnrollModalOpen}
          onVisibleChange={handleEnrollModalVisibleChange}
          style={{ width: "650px" }}
        >
          <OwcCard style={{ maxWidth: "100%" }}>
            <OwcComponentHeader slot="header">
              <OwcIcon name="user" family="outlined" slot="prefix" />
              Enroll Patient
            </OwcComponentHeader>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 24,
                padding: "24px 24px",
              }}
              aria-labelledby="enrollPatientHeader"
            >
              {/* Form row: First Name & Last Name */}
              <div style={{ display: "flex", gap: 16, width: "100%" }}>
                <OwcInput
                  id="firstName"
                  name="firstName"
                  variant="outlined"
                  label="First Name*"
                  placeholder="Enter first name"
                  required
                  validationTrigger="blur"
                  value={enrollmentForm.firstName}
                  onInput={(e: any) => handleInputChange(e)}
                  style={{ width: "100%" }}
                  aria-labelledby="firstNameLabel"
                  aria-required="true"
                  aria-invalid={Boolean(formErrors.firstName)}
                >
                  <OwcIcon slot="prefix" name="user" family="outlined" />
                  <OwcAssistiveText>
                    {formErrors.firstName
                      ? formErrors.firstName
                      : "First name is required"}
                  </OwcAssistiveText>
                </OwcInput>

                <OwcInput
                  id="lastName"
                  name="lastName"
                  variant="outlined"
                  label="Last Name*"
                  placeholder="Enter last name"
                  required
                  validationTrigger="blur"
                  value={enrollmentForm.lastName}
                  onInput={(e: any) => handleInputChange(e)}
                  style={{ width: "100%" }}
                  aria-labelledby="lastNameLabel"
                  aria-required="true"
                  aria-invalid={Boolean(formErrors.lastName)}
                >
                  <OwcIcon slot="prefix" name="user" family="outlined" />
                  <OwcAssistiveText>
                    {formErrors.lastName
                      ? formErrors.lastName
                      : "Last name is required"}
                  </OwcAssistiveText>
                </OwcInput>
              </div>

              {/* Form row: MRN */}
              <div style={{ width: "100%" }}>
                <OwcInput
                  id="mrn"
                  name="mrn"
                  variant="outlined"
                  label="Medical Record Number (MRN)*"
                  placeholder="Enter patient's MRN"
                  required
                  validationTrigger="blur"
                  value={enrollmentForm.mrn}
                  onInput={(e: any) => handleInputChange(e)}
                  style={{ width: "100%" }}
                  aria-labelledby="mrnLabel"
                  aria-required="true"
                  aria-invalid={Boolean(formErrors.mrn)}
                >
                  <OwcIcon slot="prefix" name="data" family="outlined" />
                  <OwcAssistiveText>
                    {formErrors.mrn ? formErrors.mrn : "MRN is required"}
                  </OwcAssistiveText>
                </OwcInput>
              </div>

              {/* Form row: Email & Phone */}
              <div style={{ display: "flex", gap: 16, width: "100%" }}>
                <OwcInput
                  id="email"
                  name="email"
                  variant="outlined"
                  type="email"
                  label="Email Address*"
                  placeholder="Enter email address"
                  required
                  validationTrigger="blur"
                  value={enrollmentForm.email}
                  onInput={(e: any) => handleInputChange(e)}
                  style={{ width: "100%" }}
                  aria-labelledby="emailLabel"
                  aria-required="true"
                  aria-invalid={Boolean(formErrors.email)}
                >
                  <OwcIcon slot="prefix" name="mail" family="outlined" />
                  <OwcAssistiveText>
                    {formErrors.email ? formErrors.email : "Email is required"}
                  </OwcAssistiveText>
                </OwcInput>

                <OwcInput
                  id="phone"
                  name="phone"
                  variant="outlined"
                  label="Phone Number*"
                  placeholder="Enter phone number"
                  required
                  validationTrigger="blur"
                  value={enrollmentForm.phone}
                  onInput={(e: any) => handleInputChange(e)}
                  style={{ width: "100%" }}
                  aria-labelledby="phoneLabel"
                  aria-required="true"
                  aria-invalid={Boolean(formErrors.phone)}
                >
                  <OwcIcon slot="prefix" name="circle_info" family="outlined" />
                  <OwcAssistiveText>
                    {formErrors.phone
                      ? formErrors.phone
                      : "Phone number is required"}
                  </OwcAssistiveText>
                </OwcInput>
              </div>

              {/* Form row: Date of Birth */}
              <div style={{ width: "100%" }}>
                <OwcInput
                  id="dateOfBirth"
                  name="dateOfBirth"
                  variant="outlined"
                  type="date"
                  label="Date of Birth*"
                  placeholder="YYYY-MM-DD"
                  required
                  validationTrigger="blur"
                  value={enrollmentForm.dateOfBirth}
                  onInput={(e: any) => handleInputChange(e)}
                  style={{ width: "100%" }}
                  aria-labelledby="dateOfBirthLabel"
                  aria-required="true"
                  aria-invalid={Boolean(formErrors.dateOfBirth)}
                >
                  <OwcIcon slot="prefix" name="table_chart" family="outlined" />
                  <OwcAssistiveText>
                    {formErrors.dateOfBirth
                      ? formErrors.dateOfBirth
                      : "Date of birth is required (YYYY-MM-DD)"}
                  </OwcAssistiveText>
                </OwcInput>
              </div>

              {/* Form row: Consent checkbox */}
              <div style={{ width: "100%" }}>
                <OwcCheckbox
                  style={{ alignSelf: "start" }}
                  aria-label="Patient consent confirmation"
                >
                  Patient understands they will be enrolled in GDM monitoring
                </OwcCheckbox>
              </div>

              {/* Submit error message */}
              {formErrors.submit && (
                <div
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    backgroundColor: "var(--owc-color-critical-light)",
                    borderRadius: "4px",
                  }}
                  role="alert"
                  aria-live="assertive"
                >
                  <OwcTypography style={{ color: "var(--owc-color-critical)" }}>
                    {formErrors.submit}
                  </OwcTypography>
                </div>
              )}

              {/* Form buttons */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  width: "100%",
                  marginTop: 8,
                }}
              >
                <OwcButton
                  type="button"
                  variant="secondary"
                  onClick={clearEnrollmentForm}
                  aria-label="Clear form fields"
                >
                  Clear Form
                </OwcButton>
                <div style={{ display: "flex", gap: 8 }}>
                  <OwcButton
                    type="button"
                    variant="secondary"
                    onClick={() => setIsEnrollModalOpen(false)}
                    aria-label="Cancel enrollment"
                  >
                    Cancel
                  </OwcButton>
                  <OwcButton
                    type="submit"
                    variant="primary"
                    disabled={createPatientMutation.isLoading}
                    aria-label="Submit enrollment form"
                  >
                    {createPatientMutation.isLoading
                      ? "Enrolling..."
                      : "Enroll Patient"}
                  </OwcButton>
                </div>
              </div>
            </form>
          </OwcCard>
        </OwcModalDialog>
      </OwcTable>
    </>
  );
};

export default PatientList;
